import * as React from 'react'
import { Alert, Button, ProgressBar } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import Footer from 'components/Footer'
import Header from 'components/Header'
import loginData from 'utilities/loginData'
import bpoData from 'utilities/bpoData'
import { useBpoStatus } from 'hooks/useBpoStatus'

const SceneComponent: React.FC = () => {
  const navigate = useNavigate()
  
  // ポーリング間隔（5分）
  const POLLING_INTERVAL = 5 * 60 * 1000 // 5分
  
  // カスタムフックを使用
  const {
    status,
    message,
    progress,
    estimatedTime,
    isLoading,
    requestId,
    bpoVerificationId,
    originalVerificationId,
    setRequestId,
    checkBpoStatus
  } = useBpoStatus(POLLING_INTERVAL)
  
  const [localIsLoading, setLocalIsLoading] = React.useState<boolean>(false)

  // TRUSTDOCK API関連の機能は削除（BPO APIを使用）

  // ブラウザバック制御
  React.useEffect(() => {
    // popstateイベントでブラウザバックを検知して無効化
    const handlePopState = () => {
      // 履歴を進めて現在のページに留まる
      window.history.pushState(null, '', window.location.href)
    }

    // 履歴スタックに現在の状態を追加
    window.history.pushState(null, '', window.location.href)
    
    // popstateイベントリスナーを登録
    window.addEventListener('popstate', handlePopState)

    // クリーンアップ
    return () => {
      window.removeEventListener('popstate', handlePopState)
    }
  }, [])

  // ページ離脱時の警告
  React.useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // 審査中の場合のみ警告を表示
      if (status === 'processing' || status === 'in_progress' || status === 'pending') {
        e.preventDefault()
        // returnValueは非推奨だが、一部のブラウザでまだ必要
        const message = '審査中です。このページを離れると審査状況の確認ができなくなる可能性があります。'
        e.returnValue = message
        return message
      }
      return
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [status])

  React.useEffect(() => {
    if (!loginData.isLoggedIn()) {
      navigate('/')
      return
    }
    
    // セッションストレージからrequestIdを取得
    const savedRequestId = bpoData.getBpoRequestId()
    
    // BPO requestIdがある場合（メインフロー）
    if (savedRequestId) {
      // カスタムフックのsetRequestIdを使用
      setRequestId(savedRequestId)
    } else {
      // requestIdがない場合は本人確認画面に戻る
      navigate('/registerIdentificationForIndividual')
    }
  }, [navigate, setRequestId])

  /**
   * タブ表示制御（ポーリングはuseBpoStatus内で管理）
   */
  React.useEffect(() => {
    // タブの表示/非表示を検知
    const handleVisibilityChange = () => {
      if (!requestId || status === 'approved' || status === 'rejected') {
        return
      }
      
      if (!document.hidden) {
        // タブが表示されたら即座に状態確認
        checkBpoStatus(requestId)
      }
    }

    // イベントリスナー登録
    document.addEventListener('visibilitychange', handleVisibilityChange)

    // クリーンアップ
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [requestId, status, checkBpoStatus])

  /**
   * 審査状況を確認ボタンのクリック処理
   */
  const handleCheckStatusButton = async () => {
    setLocalIsLoading(true)
    try {
      // BPO requestIdがある場合
      if (requestId) {
        await checkBpoStatus(requestId)
      }
    } finally {
      setLocalIsLoading(false)
    }
  }

  /**
   * ステータスに応じたアラートバリアント
   */
  const getAlertVariant = () => {
    switch (status) {
      case 'approved':
      case 'verified':
        return 'success'
      case 'rejected':
      case 'failed':
        return 'danger'
      case 'processing':
      case 'in_progress':
        return 'info'
      case 'accepted':
      case 'plans_selected':
      case 'document_submitted':
        return 'warning'
      default:
        return 'warning'
    }
  }

  return (
    <>
      <Header title="BPO審査中" />
      <main>
        <section className="mx-4 my-5">
          <div className="text-center mt-4">
            {/* 審査中アイコン（仮） */}
            <div className="mb-4">
              <div 
                style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  backgroundColor: '#f0f0f0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto',
                  fontSize: '24px'
                }}
              >
                🔍
              </div>
            </div>
            
            <h3 className="fw-bold mb-3">審査中です。</h3>
            <div className="note mb-4">
              <div>{estimatedTime}お待ちください。</div>
            </div>
            
            {/* プログレスバー */}
            <div className="mb-4">
              <ProgressBar now={progress} className="mb-2" />
              <div className="note">進捗: {progress}%</div>
            </div>
            
            <hr className="my-4" />
            
            {/* 現在の状況 */}
            <div className="mb-4">
              <div className="font-15 fw-bold mb-2">現在の状況</div>
              <Alert variant={getAlertVariant()} className="mb-3">
                {message}
              </Alert>
              
              {/* BPO Verification ID表示（開発環境または有効なIDがある場合） */}
              {(bpoVerificationId || originalVerificationId) && (
                <div className="text-muted small">
                  {bpoVerificationId && (
                    <div>BPO用verificationId: {bpoVerificationId}</div>
                  )}
                  {originalVerificationId && (
                    <div>元のverificationId: {originalVerificationId}</div>
                  )}
                </div>
              )}
            </div>
            
            {/* 審査状況確認ボタン */}
            <div className="mb-4">
              <Button 
                variant="primary" 
                className="w-100 py-3" 
                onClick={handleCheckStatusButton}
                disabled={localIsLoading || isLoading || status === 'approved' || status === 'verified'}
              >
                {(localIsLoading || isLoading) ? '確認中...' : '審査状況を確認'}
              </Button>
            </div>
            
            {/* エラー時の問い合わせボタン */}
            {(status === 'error' || status === 'timeout' || status === 'failed') && (
              <div className="mb-4">
                <Alert variant="danger" className="mb-3">
                  <Alert.Heading>エラーが発生しました</Alert.Heading>
                  <p className="mb-0">
                    審査の処理中に問題が発生しました。お手数ですが、サポートまでお問い合わせください。
                  </p>
                </Alert>
                <Button 
                  variant="primary" 
                  className="w-100 py-3" 
                  onClick={() => navigate(`/contactUs?context=bpo-processing&referenceId=${requestId}`)}
                >
                  お問い合わせ
                </Button>
              </div>
            )}
            
            {/* 注意事項 */}
            <div className="note text-muted mt-4">
              <div>※画面を閉じても審査は継続されます</div>
              <div>※審査完了時は「審査状況を確認」ボタンを押して結果をご確認ください</div>
              <div>※通常は15～30分程度で完了しますが、最大3時間かかる場合があります</div>
            </div>
            
          </div>
        </section>
      </main>
      <Footer className="mt-5" />
    </>
  )
}

export default SceneComponent